import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  Button,
  Modal,
  BlockStack,
  Text,
  InlineStack,
  Banner,
  Tabs,
  ButtonGroup,
  EmptyState,
  Spinner,
  Tooltip,
  TextField,
  FormLayout,
  Thumbnail,
  DropZone,
  Select,
  Box,
  Checkbox,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiFetch, getAuthHeader as getAuthHeaderFn } from '../api';

// ═══════════════════════════════════════
// Types
// ═══════════════════════════════════════

interface Category {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  position: number;
  createdAt: string;
}

interface Report {
  id: string;
  reporterId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  reason: string;
  description: string | null;
  status: string;
  createdAt: string;
}

interface UnderReviewPost {
  id: string;
  title: string;
  bodyPlain: string;
  status: string;
  moderationFlags: Record<string, number> | null;
  authorId: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  authorEmail: string;
  createdAt: string;
}

interface Ban {
  id: string;
  userId: string;
  reason: string | null;
  bannedBy: string;
  expiresAt: string | null;
  createdAt: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

interface UserSearchResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface PendingPost {
  id: string;
  title: string;
  bodyPlain: string;
  media: any[];
  createdAt: string;
  status: string;
  author: {
    id: string;
    name: string;
    email: string;
  };
}

interface Stats {
  totalPosts: number;
  totalComments: number;
  pendingReports: number;
  underReview: number;
  pendingModeration: number;
  postsToday: number;
}

// ═══════════════════════════════════════
// Helpers
// ═══════════════════════════════════════

function reasonLabel(reason: string): string {
  const map: Record<string, string> = {
    spam: 'Spam',
    inappropriate: 'Inapropiado',
    harassment: 'Acoso',
    misinformation: 'Desinformación',
    other: 'Otro',
  };
  return map[reason] ?? reason;
}

function reasonBadge(reason: string) {
  const toneMap: Record<string, 'critical' | 'warning' | 'attention' | 'info'> = {
    spam: 'warning',
    inappropriate: 'critical',
    harassment: 'critical',
    misinformation: 'attention',
    other: 'info',
  };
  return <Badge tone={toneMap[reason] ?? 'info'}>{reasonLabel(reason)}</Badge>;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ═══════════════════════════════════════
// Main Component
// ═══════════════════════════════════════

export default function CommunityModeration() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const data = await apiGet<Stats>('/panel-api/community/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    loadStats().finally(() => setLoading(false));
  }, [loadStats]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  if (loading) {
    return (
      <Page title="Comunidad">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  const tabs = [
    { id: 'posts', content: `Posts (${stats?.totalPosts ?? 0})`, panelID: 'posts-panel' },
    { id: 'comments', content: `Comentarios (${stats?.totalComments ?? 0})`, panelID: 'comments-panel' },
    { id: 'categories', content: 'Categorías', panelID: 'categories-panel' },
    { id: 'reports', content: `Reportes (${stats?.pendingReports ?? 0})`, panelID: 'reports-panel' },
    { id: 'under-review', content: `En revisión (${stats?.underReview ?? 0})`, panelID: 'review-panel' },
    { id: 'bans', content: 'Usuarios restringidos', panelID: 'bans-panel' },
    { id: 'agents', content: '🤖 Agentes', panelID: 'agents-panel' },
    { id: 'moderation', content: `⏳ Moderación (${stats?.pendingModeration ?? 0})`, panelID: 'moderation-panel' },
  ];

  return (
    <Page title="Comunidad">
      <Layout>
        {successMsg && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMsg('')}>
              {successMsg}
            </Banner>
          </Layout.Section>
        )}

        {/* Stats cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="100">
                <Text variant="headingMd" as="h3">{stats?.totalPosts ?? 0}</Text>
                <Text variant="bodySm" as="p" tone="subdued">Posts totales</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="100">
                <Text variant="headingMd" as="h3">{stats?.postsToday ?? 0}</Text>
                <Text variant="bodySm" as="p" tone="subdued">Posts hoy</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="100">
                <Text variant="headingMd" as="h3" tone="critical">{stats?.pendingReports ?? 0}</Text>
                <Text variant="bodySm" as="p" tone="subdued">Reportes pendientes</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="100">
                <Text variant="headingMd" as="h3" tone="caution">{stats?.underReview ?? 0}</Text>
                <Text variant="bodySm" as="p" tone="subdued">En revisión</Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Tabs */}
        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
              {selectedTab === 0 && <PostsTab onSuccess={(msg) => { showSuccess(msg); loadStats(); }} />}
              {selectedTab === 1 && <CommentsTab onSuccess={(msg) => { showSuccess(msg); loadStats(); }} />}
              {selectedTab === 2 && <CategoriesTab onSuccess={showSuccess} />}
              {selectedTab === 3 && <ReportsTab onSuccess={(msg) => { showSuccess(msg); loadStats(); }} />}
              {selectedTab === 4 && <UnderReviewTab onSuccess={(msg) => { showSuccess(msg); loadStats(); }} />}
              {selectedTab === 5 && <BansTab onSuccess={showSuccess} />}
              {selectedTab === 6 && <AgentsTab onSuccess={showSuccess} />}
              {selectedTab === 7 && <ModerationTab onSuccess={(msg) => { showSuccess(msg); loadStats(); }} />}
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

// ═══════════════════════════════════════
// Categories Tab
// ═══════════════════════════════════════

// ═══════════════════════════════════════
// Posts Tab (browse, view detail, edit, delete)
// ═══════════════════════════════════════

interface AdminPost {
  id: string;
  title: string;
  bodyPlain: string;
  status: string;
  isPinned: boolean;
  upvoteCount: number;
  commentCount: number;
  media: any[];
  authorName: string;
  authorEmail: string;
  categoryName: string;
  createdAt: string;
}

interface AdminComment {
  id: string;
  bodyPlain: string;
  status: string;
  upvoteCount: number;
  parentId: string | null;
  depth: number;
  authorName: string;
  authorEmail: string;
  createdAt: string;
}

function PostsTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [detailPost, setDetailPost] = useState<AdminPost | null>(null);
  const [detailComments, setDetailComments] = useState<AdminComment[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editModal, setEditModal] = useState<{ type: 'post' | 'comment'; id: string; title?: string; body: string } | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadPosts = useCallback(async (q?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const data = await apiGet<{ posts: AdminPost[] }>(`/panel-api/community/posts?${params}`);
      setPosts(data.posts);
    } catch (err) {
      console.error('Failed to load posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleSearch = () => {
    setSearchQuery(searchInput);
    loadPosts(searchInput);
  };

  const openDetail = async (postId: string) => {
    setDetailLoading(true);
    try {
      const data = await apiGet<{ post: AdminPost; comments: AdminComment[] }>(
        `/panel-api/community/posts/${postId}/detail`
      );
      setDetailPost(data.post);
      setDetailComments(data.comments);
    } catch (err) {
      console.error('Failed to load post detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailPost(null);
    setDetailComments([]);
  };

  // Edit post
  const openEditPost = (post: AdminPost) => {
    setEditModal({ type: 'post', id: post.id, title: post.title, body: post.bodyPlain });
  };

  // Edit comment
  const openEditComment = (comment: AdminComment) => {
    setEditModal({ type: 'comment', id: comment.id, body: comment.bodyPlain });
  };

  const saveEdit = async () => {
    if (!editModal) return;
    setEditSaving(true);
    try {
      if (editModal.type === 'post') {
        await apiPut(`/panel-api/community/posts/${editModal.id}`, {
          title: editModal.title,
          bodyPlain: editModal.body,
        });
        onSuccess('Post actualizado');
      } else {
        await apiPut(`/panel-api/community/comments/${editModal.id}`, {
          bodyPlain: editModal.body,
        });
        onSuccess('Comentario actualizado');
      }
      setEditModal(null);
      // Refresh detail if open
      if (detailPost) openDetail(detailPost.id);
      loadPosts(searchQuery);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setEditSaving(false);
    }
  };

  // Delete post
  const deletePost = async (postId: string) => {
    if (!confirm('¿Eliminar este post? Se ocultará de la comunidad.')) return;
    setActionLoading(postId);
    try {
      await apiDelete(`/panel-api/community/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
      if (detailPost?.id === postId) closeDetail();
      onSuccess('Post eliminado');
    } catch (err) {
      console.error('Failed to delete post:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Delete comment
  const deleteComment = async (commentId: string) => {
    if (!confirm('¿Eliminar este comentario?')) return;
    setActionLoading(commentId);
    try {
      await apiDelete(`/panel-api/community/comments/${commentId}`);
      setDetailComments(prev => prev.filter(c => c.id !== commentId));
      onSuccess('Comentario eliminado');
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Change post status
  const changePostStatus = async (postId: string, status: string) => {
    setActionLoading(postId);
    try {
      await apiPut(`/panel-api/community/posts/${postId}`, { status });
      onSuccess(`Post ${status === 'removed' ? 'eliminado' : status === 'published' ? 'publicado' : 'actualizado'}`);
      loadPosts(searchQuery);
      if (detailPost?.id === postId) openDetail(postId);
    } catch (err) {
      console.error('Failed to change status:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}><Spinner /></div>;

  return (
    <>
      {/* Search bar */}
      <div style={{ padding: '16px 0 8px', display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <TextField
            label=""
            labelHidden
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Buscar posts por título o contenido..."
            autoComplete="off"
            // @ts-expect-error Polaris TextField doesn't expose onKeyDown but it works at DOM level
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </div>
        <Button onClick={handleSearch}>Buscar</Button>
      </div>

      {posts.length === 0 ? (
        <EmptyState
          heading="Sin posts"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>{searchQuery ? 'No se encontraron posts con esa búsqueda.' : 'Aún no hay posts en la comunidad.'}</p>
        </EmptyState>
      ) : (
        <IndexTable
          itemCount={posts.length}
          headings={[
            { title: 'Post' },
            { title: 'Autor' },
            { title: 'Categoría' },
            { title: 'Estado' },
            { title: 'Stats' },
            { title: 'Fecha' },
            { title: 'Acciones' },
          ]}
          selectable={false}
        >
          {posts.map((post, idx) => (
            <IndexTable.Row key={post.id} id={post.id} position={idx}>
              <IndexTable.Cell>
                <BlockStack gap="100">
                  <Text variant="bodyMd" as="p" fontWeight="semibold" truncate>
                    {post.isPinned ? '📌 ' : ''}{post.title}
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued" truncate>
                    {post.bodyPlain?.slice(0, 80)}
                  </Text>
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p">{post.authorName}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge>{post.categoryName}</Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge tone={
                  post.status === 'published' ? 'success' :
                  post.status === 'removed' ? 'critical' :
                  post.status === 'under_review' ? 'warning' : 'info'
                }>
                  {post.status === 'published' ? 'Publicado' :
                   post.status === 'removed' ? 'Eliminado' :
                   post.status === 'under_review' ? 'En revisión' : post.status}
                </Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p">
                  ▲ {post.upvoteCount} · 💬 {post.commentCount}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p" tone="subdued">{formatDate(post.createdAt)}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <ButtonGroup>
                  <Button size="slim" onClick={() => openDetail(post.id)}>Ver</Button>
                  <Button size="slim" onClick={() => openEditPost(post)}>Editar</Button>
                  <Button size="slim" tone="critical" loading={actionLoading === post.id}
                    onClick={() => deletePost(post.id)}>
                    Eliminar
                  </Button>
                </ButtonGroup>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      )}

      {/* Post Detail Modal */}
      <Modal
        open={!!detailPost}
        onClose={closeDetail}
        title={detailPost?.title ?? 'Post'}
        size="large"
        secondaryActions={[{ content: 'Cerrar', onAction: closeDetail }]}
      >
        <Modal.Section>
          {detailLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><Spinner /></div>
          ) : detailPost ? (
            <BlockStack gap="400">
              {/* Post info */}
              <InlineStack gap="200">
                <Badge tone={detailPost.status === 'published' ? 'success' : detailPost.status === 'removed' ? 'critical' : 'warning'}>
                  {detailPost.status}
                </Badge>
                <Text variant="bodySm" as="p" tone="subdued">
                  por {detailPost.authorName} · {formatDate(detailPost.createdAt)}
                </Text>
              </InlineStack>

              {/* Post body */}
              <Card>
                <Text variant="bodyMd" as="p">{detailPost.bodyPlain}</Text>
              </Card>

              {/* Media */}
              {detailPost.media && detailPost.media.length > 0 && (
                <InlineStack gap="200">
                  {detailPost.media.map((m: any, i: number) => (
                    <img
                      key={i}
                      src={m.url}
                      alt=""
                      style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                    />
                  ))}
                </InlineStack>
              )}

              {/* Post actions */}
              <InlineStack gap="200">
                <Button onClick={() => openEditPost(detailPost)}>Editar post</Button>
                {detailPost.status === 'published' ? (
                  <Button tone="critical" onClick={() => changePostStatus(detailPost.id, 'removed')}>
                    Ocultar post
                  </Button>
                ) : (
                  <Button variant="primary" onClick={() => changePostStatus(detailPost.id, 'published')}>
                    Publicar post
                  </Button>
                )}
              </InlineStack>

              {/* Comments section */}
              <Text variant="headingMd" as="h3">Comentarios ({detailComments.length})</Text>

              {detailComments.length === 0 ? (
                <Text variant="bodySm" as="p" tone="subdued">Sin comentarios</Text>
              ) : (
                <BlockStack gap="300">
                  {detailComments.map(comment => (
                    <Card key={comment.id}>
                      <BlockStack gap="200">
                        <InlineStack gap="200" align="space-between">
                          <InlineStack gap="200">
                            {comment.depth > 0 && (
                              <Text variant="bodySm" as="p" tone="subdued">
                                {'↳'.repeat(Math.min(comment.depth, 3))}
                              </Text>
                            )}
                            <Text variant="bodySm" as="p" fontWeight="semibold">{comment.authorName}</Text>
                            <Text variant="bodySm" as="p" tone="subdued">{formatDate(comment.createdAt)}</Text>
                            {comment.status !== 'published' && (
                              <Badge tone={comment.status === 'removed' ? 'critical' : 'warning'}>{comment.status}</Badge>
                            )}
                          </InlineStack>
                          <InlineStack gap="100">
                            <Text variant="bodySm" as="p" tone="subdued">▲ {comment.upvoteCount}</Text>
                          </InlineStack>
                        </InlineStack>
                        <Text variant="bodyMd" as="p">{comment.bodyPlain}</Text>
                        <InlineStack gap="200">
                          <Button size="slim" onClick={() => openEditComment(comment)}>Editar</Button>
                          <Button size="slim" tone="critical" loading={actionLoading === comment.id}
                            onClick={() => deleteComment(comment.id)}>
                            Eliminar
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Card>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          ) : null}
        </Modal.Section>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title={editModal?.type === 'post' ? 'Editar post' : 'Editar comentario'}
        primaryAction={{
          content: 'Guardar',
          onAction: saveEdit,
          loading: editSaving,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setEditModal(null) }]}
      >
        <Modal.Section>
          <FormLayout>
            {editModal?.type === 'post' && (
              <TextField
                label="Título"
                value={editModal?.title ?? ''}
                onChange={(v) => setEditModal(prev => prev ? { ...prev, title: v } : null)}
                autoComplete="off"
              />
            )}
            <TextField
              label="Contenido"
              value={editModal?.body ?? ''}
              onChange={(v) => setEditModal(prev => prev ? { ...prev, body: v } : null)}
              multiline={6}
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </>
  );
}

// ═══════════════════════════════════════
// Comments Tab (global moderation)
// ═══════════════════════════════════════

interface GlobalComment {
  id: string;
  postId: string;
  postTitle: string;
  parentId: string | null;
  bodyPlain: string;
  status: string;
  upvoteCount: number;
  depth: number;
  authorName: string;
  authorEmail: string;
  createdAt: string;
}

function CommentsTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [comments, setComments] = useState<GlobalComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editModal, setEditModal] = useState<{ id: string; body: string } | null>(null);
  const [editBody, setEditBody] = useState('');

  const loadComments = useCallback(async (q?: string, s?: string, p?: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      if (s && s !== 'all') params.set('status', s);
      params.set('page', String(p || 1));
      const data = await apiGet<{ comments: GlobalComment[]; total: number; totalPages: number }>('/panel-api/community/comments?' + params.toString());
      setComments(data.comments);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadComments(search, statusFilter, page); }, [loadComments, search, statusFilter, page]);

  const deleteComment = async (id: string) => {
    setActionLoading(id);
    try {
      await apiDelete('/panel-api/community/comments/' + id);
      setComments(prev => prev.filter(c => c.id !== id));
      onSuccess('Comentario eliminado');
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const changeStatus = async (id: string, status: string) => {
    setActionLoading(id);
    try {
      await apiPut('/panel-api/community/comments/' + id, { status });
      setComments(prev => prev.map(c => c.id === id ? { ...c, status } : c));
      onSuccess('Estado actualizado');
    } catch (err) {
      console.error('Failed to change status:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const saveEdit = async () => {
    if (!editModal) return;
    setActionLoading(editModal.id);
    try {
      await apiPut('/panel-api/community/comments/' + editModal.id, { bodyPlain: editBody });
      setComments(prev => prev.map(c => c.id === editModal.id ? { ...c, bodyPlain: editBody } : c));
      setEditModal(null);
      onSuccess('Comentario editado');
    } catch (err) {
      console.error('Failed to edit comment:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading && comments.length === 0) {
    return <Box padding="800"><InlineStack align="center"><Spinner /></InlineStack></Box>;
  }

  return (
    <BlockStack gap="400">
      <Box paddingBlockStart="400">
        <InlineStack gap="300" blockAlign="end">
          <div style={{ flex: 1 }}>
            <TextField
              label=""
              placeholder="Buscar en comentarios..."
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              autoComplete="off"
            />
          </div>
          <Select
            label=""
            options={[
              { label: 'Todos', value: 'all' },
              { label: 'Publicados', value: 'published' },
              { label: 'Eliminados', value: 'removed' },
              { label: 'En revisión', value: 'under_review' },
            ]}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          />
        </InlineStack>
      </Box>

      <Text variant="bodySm" as="p" tone="subdued">{total} comentarios en total</Text>

      <IndexTable
        resourceName={{ singular: 'comentario', plural: 'comentarios' }}
        itemCount={comments.length}
        headings={[
          { title: 'Autor' },
          { title: 'Comentario' },
          { title: 'Post' },
          { title: 'Estado' },
          { title: 'Fecha' },
          { title: 'Acciones' },
        ]}
        selectable={false}
      >
        {comments.map((comment, idx) => (
          <IndexTable.Row id={comment.id} key={comment.id} position={idx}>
            <IndexTable.Cell>
              <BlockStack gap="050">
                <Text variant="bodySm" as="p" fontWeight="semibold">{comment.authorName}</Text>
                <Text variant="bodySm" as="p" tone="subdued">{comment.authorEmail}</Text>
              </BlockStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text variant="bodyMd" as="p">
                {comment.depth > 0 && <span style={{ color: '#8c9196' }}>{'↳'.repeat(Math.min(comment.depth, 3))} </span>}
                {comment.bodyPlain.length > 120 ? comment.bodyPlain.substring(0, 120) + '...' : comment.bodyPlain}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text variant="bodySm" as="p" tone="subdued">
                {comment.postTitle ? (comment.postTitle.length > 40 ? comment.postTitle.substring(0, 40) + '...' : comment.postTitle) : '—'}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Badge tone={comment.status === 'published' ? 'success' : comment.status === 'removed' ? 'critical' : 'warning'}>
                {comment.status === 'published' ? 'Publicado' : comment.status === 'removed' ? 'Eliminado' : 'En revisión'}
              </Badge>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text variant="bodySm" as="p" tone="subdued">{formatDate(comment.createdAt)}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <ButtonGroup>
                <Button
                  size="micro"
                  onClick={() => { setEditModal({ id: comment.id, body: comment.bodyPlain }); setEditBody(comment.bodyPlain); }}
                >
                  Editar
                </Button>
                {comment.status === 'published' ? (
                  <Button
                    size="micro"
                    tone="critical"
                    loading={actionLoading === comment.id}
                    onClick={() => changeStatus(comment.id, 'removed')}
                  >
                    Ocultar
                  </Button>
                ) : comment.status === 'removed' ? (
                  <Button
                    size="micro"
                    loading={actionLoading === comment.id}
                    onClick={() => changeStatus(comment.id, 'published')}
                  >
                    Restaurar
                  </Button>
                ) : (
                  <>
                    <Button size="micro" loading={actionLoading === comment.id} onClick={() => changeStatus(comment.id, 'published')}>Aprobar</Button>
                    <Button size="micro" tone="critical" loading={actionLoading === comment.id} onClick={() => changeStatus(comment.id, 'removed')}>Rechazar</Button>
                  </>
                )}
                <Button
                  size="micro"
                  tone="critical"
                  loading={actionLoading === comment.id}
                  onClick={() => deleteComment(comment.id)}
                >
                  🗑
                </Button>
              </ButtonGroup>
            </IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>

      {totalPages > 1 && (
        <InlineStack align="center" gap="200">
          <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</Button>
          <Text variant="bodySm" as="p">Página {page} de {totalPages}</Text>
          <Button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Siguiente →</Button>
        </InlineStack>
      )}

      {/* Edit modal */}
      {editModal && (
        <Modal
          open
          onClose={() => setEditModal(null)}
          title="Editar comentario"
          primaryAction={{ content: 'Guardar', onAction: saveEdit, loading: actionLoading === editModal.id }}
          secondaryActions={[{ content: 'Cancelar', onAction: () => setEditModal(null) }]}
        >
          <Modal.Section>
            <TextField
              label="Contenido"
              value={editBody}
              onChange={setEditBody}
              multiline={4}
              autoComplete="off"
            />
          </Modal.Section>
        </Modal>
      )}
    </BlockStack>
  );
}

// ═══════════════════════════════════════
// Categories Tab
// ═══════════════════════════════════════

function CategoriesTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCat, setEditingCat] = useState<Category | null>(null);
  const [form, setForm] = useState({ slug: '', name: '', description: '', position: '0' });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [modalImageFile, setModalImageFile] = useState<File | null>(null);
  const [modalImagePreview, setModalImagePreview] = useState<string | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiGet<{ categories: Category[] }>('/panel-api/community/categories');
      setCategories(data.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const openCreate = () => {
    setEditingCat(null);
    setForm({ slug: '', name: '', description: '', position: String(categories.length) });
    setModalImageFile(null);
    setModalImagePreview(null);
    setShowModal(true);
  };

  const openEdit = (cat: Category) => {
    setEditingCat(cat);
    setForm({
      slug: cat.slug,
      name: cat.name,
      description: cat.description ?? '',
      position: String(cat.position),
    });
    setModalImageFile(null);
    setModalImagePreview(cat.iconUrl ?? null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let catId: string;

      if (editingCat) {
        await apiPut(`/panel-api/community/categories/${editingCat.id}`, {
          name: form.name,
          description: form.description || undefined,
          position: parseInt(form.position) || 0,
        });
        catId = editingCat.id;
        onSuccess('Categoría actualizada');
      } else {
        const result = await apiPost<{ category: Category }>('/panel-api/community/categories', {
          slug: form.slug,
          name: form.name,
          description: form.description || undefined,
          position: parseInt(form.position) || 0,
        });
        catId = result.category.id;
        onSuccess('Categoría creada');
      }

      // Upload image if selected
      if (modalImageFile) {
        await handleImageUpload(catId, modalImageFile);
      }

      setShowModal(false);
      loadCategories();
    } catch (err: any) {
      console.error('Failed to save category:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (catId: string) => {
    if (!confirm('¿Eliminar esta categoría? Solo es posible si no tiene posts.')) return;
    try {
      await apiDelete(`/panel-api/community/categories/${catId}`);
      onSuccess('Categoría eliminada');
      loadCategories();
    } catch (err: any) {
      alert(err.message ?? 'No se pudo eliminar la categoría');
    }
  };

  const handleImageUpload = async (catId: string, file: File) => {
    setUploadingImage(catId);
    try {
      // Proxy upload through backend (R2 doesn't support browser direct PUT)
      const formData = new FormData();
      formData.append('file', file);

      const authHeader = await getAuthHeaderFn();
      const API_BASE = import.meta.env.VITE_API_URL ?? 'https://app.ohmygoods.com';

      const res = await fetch(`${API_BASE}/panel-api/community/categories/${catId}/upload-image`, {
        method: 'POST',
        headers: { Authorization: authHeader },
        body: formData,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Upload failed: ${errText}`);
      }

      onSuccess('Imagen subida');
      loadCategories();
    } catch (err) {
      console.error('Failed to upload image:', err);
    } finally {
      setUploadingImage(null);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}><Spinner /></div>;

  return (
    <>
      <div style={{ padding: '16px 0 8px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={openCreate}>Crear categoría</Button>
      </div>

      {categories.length === 0 ? (
        <EmptyState
          heading="Sin categorías"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          action={{ content: 'Crear primera categoría', onAction: openCreate }}
        >
          <p>Crea categorías para organizar los posts de la comunidad.</p>
        </EmptyState>
      ) : (
        <IndexTable
          itemCount={categories.length}
          headings={[
            { title: 'Imagen' },
            { title: 'Nombre' },
            { title: 'Slug' },
            { title: 'Descripción' },
            { title: 'Posición' },
            { title: 'Acciones' },
          ]}
          selectable={false}
        >
          {categories.map((cat, idx) => (
            <IndexTable.Row key={cat.id} id={cat.id} position={idx}>
              <IndexTable.Cell>
                <div style={{ width: 48, height: 48 }}>
                  {cat.iconUrl ? (
                    <Thumbnail source={cat.iconUrl} alt={cat.name} size="small" />
                  ) : (
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      background: '#F3F6E7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                    }}>
                      📁
                    </div>
                  )}
                  <div style={{ marginTop: 4 }}>
                    <label
                      style={{ fontSize: 11, color: '#7E942B', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {uploadingImage === cat.id ? 'Subiendo...' : 'Cambiar'}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(cat.id, file);
                        }}
                      />
                    </label>
                  </div>
                </div>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodyMd" as="p" fontWeight="semibold">{cat.name}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge>{cat.slug}</Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p" tone="subdued" truncate>
                  {cat.description ?? '—'}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p">{cat.position}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <ButtonGroup>
                  <Button size="slim" onClick={() => openEdit(cat)}>Editar</Button>
                  <Button size="slim" tone="critical" onClick={() => handleDelete(cat.id)}>Eliminar</Button>
                </ButtonGroup>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingCat ? 'Editar categoría' : 'Nueva categoría'}
        primaryAction={{
          content: editingCat ? 'Guardar' : 'Crear',
          onAction: handleSave,
          loading: saving,
          disabled: !form.name.trim() || (!editingCat && !form.slug.trim()),
        }}
        secondaryActions={[
          { content: 'Cancelar', onAction: () => setShowModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {!editingCat && (
              <TextField
                label="Slug"
                value={form.slug}
                onChange={(v) => setForm({ ...form, slug: v })}
                helpText="Identificador único (ej: recetas, entreno)"
                autoComplete="off"
              />
            )}
            <TextField
              label="Nombre"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              autoComplete="off"
            />
            <TextField
              label="Descripción"
              value={form.description}
              onChange={(v) => setForm({ ...form, description: v })}
              multiline={3}
              autoComplete="off"
            />
            <TextField
              label="Posición"
              type="number"
              value={form.position}
              onChange={(v) => setForm({ ...form, position: v })}
              autoComplete="off"
            />

            {/* Image upload */}
            <BlockStack gap="200">
              <Text variant="bodyMd" as="p" fontWeight="semibold">Imagen de categoría</Text>
              {modalImagePreview && !modalImageFile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <img
                    src={modalImagePreview}
                    alt="Imagen actual"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <Text variant="bodySm" as="p" tone="subdued">Imagen actual</Text>
                </div>
              )}
              {modalImageFile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <img
                    src={URL.createObjectURL(modalImageFile)}
                    alt="Nueva imagen"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <div>
                    <Text variant="bodySm" as="p">{modalImageFile.name}</Text>
                    <button
                      onClick={() => setModalImageFile(null)}
                      style={{
                        background: 'none', border: 'none', color: '#bf0711',
                        cursor: 'pointer', fontSize: 12, padding: 0, marginTop: 4,
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
              {!modalImageFile && (
                <DropZone
                  accept="image/*"
                  type="image"
                  onDrop={(_files, acceptedFiles) => {
                    if (acceptedFiles.length > 0) {
                      setModalImageFile(acceptedFiles[0]);
                    }
                  }}
                  allowMultiple={false}
                >
                  <DropZone.FileUpload actionHint="o arrastra una imagen" />
                </DropZone>
              )}
            </BlockStack>
          </FormLayout>
        </Modal.Section>
      </Modal>
    </>
  );
}

// ═══════════════════════════════════════
// Reports Tab
// ═══════════════════════════════════════

function ReportsTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ items: Report[] }>('/panel-api/community/reports?status=pending')
      .then(data => setReports(data.items))
      .catch(err => console.error('Failed to load reports:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleAction = async (reportId: string, status: 'reviewed' | 'dismissed', action: 'keep' | 'remove' | 'none') => {
    setActionLoading(reportId);
    try {
      await apiPut(`/panel-api/community/reports/${reportId}`, { status, action });
      setReports(prev => prev.filter(r => r.id !== reportId));
      onSuccess(action === 'remove' ? 'Contenido eliminado' : 'Reporte procesado');
    } catch (err) {
      console.error('Failed to process report:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}><Spinner /></div>;

  if (reports.length === 0) {
    return (
      <EmptyState
        heading="Sin reportes pendientes"
        image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      >
        <p>No hay reportes de la comunidad por revisar.</p>
      </EmptyState>
    );
  }

  return (
    <IndexTable
      itemCount={reports.length}
      headings={[
        { title: 'Tipo' },
        { title: 'Motivo' },
        { title: 'Descripción' },
        { title: 'Fecha' },
        { title: 'Acciones' },
      ]}
      selectable={false}
    >
      {reports.map((report, idx) => (
        <IndexTable.Row key={report.id} id={report.id} position={idx}>
          <IndexTable.Cell>
            <Badge>{report.targetType === 'post' ? 'Post' : 'Comentario'}</Badge>
          </IndexTable.Cell>
          <IndexTable.Cell>{reasonBadge(report.reason)}</IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodySm" as="p" truncate>{report.description ?? '—'}</Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodySm" as="p" tone="subdued">{formatDate(report.createdAt)}</Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <ButtonGroup>
              <Button size="slim" tone="critical" loading={actionLoading === report.id}
                onClick={() => handleAction(report.id, 'reviewed', 'remove')}>
                Eliminar
              </Button>
              <Button size="slim" loading={actionLoading === report.id}
                onClick={() => handleAction(report.id, 'dismissed', 'keep')}>
                Descartar
              </Button>
            </ButtonGroup>
          </IndexTable.Cell>
        </IndexTable.Row>
      ))}
    </IndexTable>
  );
}

// ═══════════════════════════════════════
// Under Review Tab
// ═══════════════════════════════════════

function UnderReviewTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [posts, setPosts] = useState<UnderReviewPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ posts: UnderReviewPost[] }>('/panel-api/community/posts/under-review')
      .then(data => setPosts(data.posts))
      .catch(err => console.error('Failed to load posts:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleAction = async (postId: string, status: 'published' | 'removed') => {
    setActionLoading(postId);
    try {
      await apiPut(`/panel-api/community/posts/${postId}/status`, { status });
      setPosts(prev => prev.filter(p => p.id !== postId));
      onSuccess(status === 'published' ? 'Post aprobado' : 'Post eliminado');
    } catch (err) {
      console.error('Failed to update post:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}><Spinner /></div>;

  if (posts.length === 0) {
    return (
      <EmptyState
        heading="Nada en revisión"
        image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      >
        <p>No hay posts pendientes de revisión.</p>
      </EmptyState>
    );
  }

  return (
    <IndexTable
      itemCount={posts.length}
      headings={[
        { title: 'Título' },
        { title: 'Autor' },
        { title: 'Flags IA' },
        { title: 'Fecha' },
        { title: 'Acciones' },
      ]}
      selectable={false}
    >
      {posts.map((post, idx) => (
        <IndexTable.Row key={post.id} id={post.id} position={idx}>
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" as="p" fontWeight="semibold" truncate>{post.title}</Text>
              <Text variant="bodySm" as="p" tone="subdued" truncate>{post.bodyPlain?.slice(0, 100)}</Text>
            </BlockStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodySm" as="p">{post.authorFirstName ?? ''} {post.authorLastName ?? ''}</Text>
            <Text variant="bodySm" as="p" tone="subdued">{post.authorEmail}</Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            {post.moderationFlags ? (
              <InlineStack gap="100" wrap>
                {Object.entries(post.moderationFlags)
                  .filter(([_, v]) => (v as number) > 0.3)
                  .map(([k, v]) => (
                    <Tooltip key={k} content={`${k}: ${((v as number) * 100).toFixed(0)}%`}>
                      <Badge tone={(v as number) > 0.7 ? 'critical' : 'warning'}>{k}</Badge>
                    </Tooltip>
                  ))}
              </InlineStack>
            ) : (
              <Text variant="bodySm" as="p" tone="subdued">—</Text>
            )}
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text variant="bodySm" as="p" tone="subdued">{formatDate(post.createdAt)}</Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <ButtonGroup>
              <Button size="slim" variant="primary" loading={actionLoading === post.id}
                onClick={() => handleAction(post.id, 'published')}>
                Aprobar
              </Button>
              <Button size="slim" tone="critical" loading={actionLoading === post.id}
                onClick={() => handleAction(post.id, 'removed')}>
                Eliminar
              </Button>
            </ButtonGroup>
          </IndexTable.Cell>
        </IndexTable.Row>
      ))}
    </IndexTable>
  );
}

// ═══════════════════════════════════════
// Bans Tab
// ═══════════════════════════════════════

function BansTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [bans, setBans] = useState<Ban[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banForm, setBanForm] = useState({ userId: '', reason: '', duration: 'permanent' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [saving, setSaving] = useState(false);

  const loadBans = useCallback(async () => {
    try {
      const data = await apiGet<{ bans: Ban[] }>('/panel-api/community/bans');
      setBans(data.bans);
    } catch (err) {
      console.error('Failed to load bans:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBans(); }, [loadBans]);

  // Debounced user search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiGet<{ users: UserSearchResult[] }>(
          `/panel-api/community/users/search?q=${encodeURIComponent(searchQuery)}`
        );
        setSearchResults(data.users);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleBan = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      let expiresAt: string | undefined;
      if (banForm.duration !== 'permanent') {
        const days = parseInt(banForm.duration);
        const d = new Date();
        d.setDate(d.getDate() + days);
        expiresAt = d.toISOString();
      }

      await apiPost('/panel-api/community/bans', {
        userId: selectedUser.id,
        reason: banForm.reason || undefined,
        expiresAt,
      });

      onSuccess(`Usuario ${selectedUser.email} restringido`);
      setShowBanModal(false);
      setSelectedUser(null);
      setSearchQuery('');
      setBanForm({ userId: '', reason: '', duration: 'permanent' });
      loadBans();
    } catch (err) {
      console.error('Failed to ban user:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUnban = async (banId: string) => {
    try {
      await apiDelete(`/panel-api/community/bans/${banId}`);
      setBans(prev => prev.filter(b => b.id !== banId));
      onSuccess('Restricción eliminada');
    } catch (err) {
      console.error('Failed to unban:', err);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}><Spinner /></div>;

  return (
    <>
      <div style={{ padding: '16px 0 8px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" tone="critical" onClick={() => setShowBanModal(true)}>
          Restringir usuario
        </Button>
      </div>

      {bans.length === 0 ? (
        <EmptyState
          heading="Sin usuarios restringidos"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>No hay usuarios con acceso restringido a la comunidad.</p>
        </EmptyState>
      ) : (
        <IndexTable
          itemCount={bans.length}
          headings={[
            { title: 'Usuario' },
            { title: 'Motivo' },
            { title: 'Duración' },
            { title: 'Fecha' },
            { title: 'Acciones' },
          ]}
          selectable={false}
        >
          {bans.map((ban, idx) => (
            <IndexTable.Row key={ban.id} id={ban.id} position={idx}>
              <IndexTable.Cell>
                <BlockStack gap="100">
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    {ban.firstName ?? ''} {ban.lastName ?? ''}
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued">{ban.email}</Text>
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p">{ban.reason ?? '—'}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                {ban.expiresAt ? (
                  <Badge>{`Hasta ${formatDate(ban.expiresAt)}`}</Badge>
                ) : (
                  <Badge tone="critical">Permanente</Badge>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p" tone="subdued">{formatDate(ban.createdAt)}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Button size="slim" onClick={() => handleUnban(ban.id)}>
                  Desbloquear
                </Button>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      )}

      {/* Ban Modal */}
      <Modal
        open={showBanModal}
        onClose={() => { setShowBanModal(false); setSelectedUser(null); setSearchQuery(''); }}
        title="Restringir usuario"
        primaryAction={{
          content: 'Restringir',
          onAction: handleBan,
          loading: saving,
          disabled: !selectedUser,
          destructive: true,
        }}
        secondaryActions={[
          { content: 'Cancelar', onAction: () => { setShowBanModal(false); setSelectedUser(null); setSearchQuery(''); } },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {/* User search */}
            <TextField
              label="Buscar usuario"
              value={searchQuery}
              onChange={(v) => { setSearchQuery(v); setSelectedUser(null); }}
              placeholder="Email o nombre..."
              autoComplete="off"
            />

            {/* Search results */}
            {searchResults.length > 0 && !selectedUser && (
              <BlockStack gap="200">
                {searchResults.map(user => (
                  <div
                    key={user.id}
                    onClick={() => { setSelectedUser(user); setSearchQuery(user.email); setSearchResults([]); }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderRadius: 8,
                      background: '#F8F8F8',
                      border: '1px solid #E8E8E8',
                    }}
                  >
                    <Text variant="bodySm" as="p" fontWeight="semibold">
                      {user.firstName ?? ''} {user.lastName ?? ''}
                    </Text>
                    <Text variant="bodySm" as="p" tone="subdued">{user.email}</Text>
                  </div>
                ))}
              </BlockStack>
            )}

            {selectedUser && (
              <Banner tone="info">
                Seleccionado: <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email})
              </Banner>
            )}

            <TextField
              label="Motivo (opcional)"
              value={banForm.reason}
              onChange={(v) => setBanForm({ ...banForm, reason: v })}
              multiline={2}
              autoComplete="off"
            />

            <Select
              label="Duración"
              value={banForm.duration}
              onChange={(v) => setBanForm({ ...banForm, duration: v })}
              options={[
                { label: 'Permanente', value: 'permanent' },
                { label: '7 días', value: '7' },
                { label: '30 días', value: '30' },
                { label: '90 días', value: '90' },
              ]}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </>
  );
}

// ═══════════════════════════════════════
// Agents Tab
// ═══════════════════════════════════════

interface Agent {
  id: string;
  code: string;
  name: string;
  age: number | null;
  city: string | null;
  market: string;
  customerId: string | null;
  avatarUrl: string | null;
  personality: string;
  visualKit: any;
  portraitPrompt: string | null;
  writingStyle: any;
  topics: string[];
  products: string[];
  strictRules: string | null;
  circles: string[];
  scheduleConfig: any;
  active: boolean;
  lastActionAt: string | null;
  createdAt: string;
}

function AgentsTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState<string | null>(null);
  const [runningAgent, setRunningAgent] = useState<string | null>(null);
  const [runMenuAgent, setRunMenuAgent] = useState<string | null>(null);
  const [modalAvatarFile, setModalAvatarFile] = useState<File | null>(null);
  const [modalAvatarPreview, setModalAvatarPreview] = useState<string | null>(null);
  const [selectedMarket, setSelectedMarket] = useState('ES');
  const [showProducts, setShowProducts] = useState(false);
  const [categories, setCategories] = useState<{ id: string; slug: string; name: string; market: string }[]>([]);

  const emptyForm = {
    code: '', name: '', age: '', city: '', market: 'ES',
    personality: '', visualKit: '', portraitPrompt: '',
    topics: '', products: '', strictRules: '', circles: [] as string[],
  };
  const [form, setForm] = useState(emptyForm);

  const loadAgents = useCallback(async () => {
    try {
      const data = await apiGet<{ agents: Agent[] }>(`/panel-api/community/agents?market=${selectedMarket}`);
      setAgents(data.agents);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedMarket]);

  useEffect(() => { setLoading(true); loadAgents(); }, [loadAgents]);

  // Load categories for circles dropdown
  useEffect(() => {
    apiGet<{ categories: { id: string; slug: string; name: string; market: string }[] }>('/panel-api/community/categories')
      .then((data) => setCategories(data.categories))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditingAgent(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (agent: Agent) => {
    setEditingAgent(agent);
    setForm({
      code: agent.code,
      name: agent.name,
      age: agent.age?.toString() ?? '',
      city: agent.city ?? '',
      market: agent.market,
      personality: agent.personality,
      visualKit: agent.visualKit ? JSON.stringify(agent.visualKit, null, 2) : '',
      portraitPrompt: agent.portraitPrompt ?? '',
      topics: (agent.topics ?? []).join(', '),
      products: (agent.products ?? []).join(', '),
      strictRules: agent.strictRules ?? '',
      circles: (agent.circles ?? []) as string[],
    });
    setModalAvatarFile(null);
    setModalAvatarPreview(agent.avatarUrl ?? null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        name: form.name,
        age: form.age ? parseInt(form.age) : undefined,
        city: form.city || undefined,
        market: form.market,
        personality: form.personality,
        visualKit: form.visualKit ? JSON.parse(form.visualKit) : undefined,
        portraitPrompt: form.portraitPrompt || undefined,
        topics: form.topics ? form.topics.split(',').map(s => s.trim()).filter(Boolean) : [],
        products: form.products ? form.products.split(',').map(s => s.trim()).filter(Boolean) : [],
        strictRules: form.strictRules || undefined,
        circles: form.circles,
      };

      let agentId: string;
      if (editingAgent) {
        await apiPut(`/panel-api/community/agents/${editingAgent.id}`, payload);
        agentId = editingAgent.id;
        onSuccess('Agente actualizado');
      } else {
        const result = await apiPost<{ agent: Agent }>('/panel-api/community/agents', payload);
        agentId = result.agent.id;
        onSuccess('Agente creado');
      }

      // Upload avatar if selected
      if (modalAvatarFile) {
        await handleAvatarUpload(agentId, modalAvatarFile);
      }

      setShowModal(false);
      loadAgents();
    } catch (err: any) {
      console.error('Failed to save agent:', err);
      alert(err.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleRunAgent = async (agent: Agent, actionType?: string) => {
    setRunningAgent(agent.id);
    setRunMenuAgent(null);
    try {
      const result = await apiPost<{ ok: boolean; action?: string; error?: string }>(
        `/panel-api/community/agents/${agent.id}/run`,
        actionType ? { actionType } : {}
      );
      if (result.ok) {
        onSuccess(`${agent.name} ejecutado: ${result.action ?? 'acción completada'}`);
      } else {
        onSuccess(`${agent.name}: ${result.error ?? 'sin resultado'}`);
      }
      loadAgents();
    } catch (err: any) {
      console.error('Failed to run agent:', err);
      alert(`Error al ejecutar ${agent.name}: ${err.message ?? 'Error desconocido'}`);
    } finally {
      setRunningAgent(null);
    }
  };

  const handleToggle = async (agent: Agent) => {
    try {
      const { active } = await apiPost<{ active: boolean }>(`/panel-api/community/agents/${agent.id}/toggle`);
      onSuccess(active ? `${agent.name} activado` : `${agent.name} desactivado`);
      loadAgents();
    } catch (err) {
      console.error('Failed to toggle agent:', err);
    }
  };

  const handleAvatarUpload = async (agentId: string, file: File) => {
    setUploadingAvatar(agentId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const authHeader = await getAuthHeaderFn();
      const API_BASE = import.meta.env.VITE_API_URL ?? 'https://app.ohmygoods.com';
      const res = await fetch(`${API_BASE}/panel-api/community/agents/${agentId}/upload-avatar`, {
        method: 'POST',
        headers: { Authorization: authHeader },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      onSuccess('Avatar subido');
      loadAgents();
    } catch (err) {
      console.error('Failed to upload avatar:', err);
    } finally {
      setUploadingAvatar(null);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}><Spinner /></div>;

  return (
    <>
      <div style={{ padding: '16px 0 8px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['ES', 'DE'].map(m => (
              <button
                key={m}
                onClick={() => setSelectedMarket(m)}
                style={{
                  padding: '4px 12px', borderRadius: '6px', border: '1px solid #ccc',
                  background: selectedMarket === m ? '#7E942B' : '#fff',
                  color: selectedMarket === m ? '#fff' : '#333',
                  fontWeight: selectedMarket === m ? 600 : 400,
                  cursor: 'pointer', fontSize: '13px',
                }}
              >
                {m === 'ES' ? '🇪🇸 España' : '🇩🇪 Deutschland'}
              </button>
            ))}
          </div>
          <Text variant="bodyMd" as="p" tone="subdued">
            {agents.filter(a => a.active).length} activos de {agents.length} agentes
          </Text>
        </div>
        <ButtonGroup>
          <Button onClick={() => setShowProducts(true)}>📦 Productos</Button>
          <Button variant="primary" onClick={openCreate}>Crear agente</Button>
        </ButtonGroup>
      </div>

      {agents.length === 0 ? (
        <EmptyState
          heading="Sin agentes"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          action={{ content: 'Crear primer agente', onAction: openCreate }}
        >
          <p>Crea agentes de IA para generar contenido en la comunidad.</p>
        </EmptyState>
      ) : (
        <IndexTable
          itemCount={agents.length}
          headings={[
            { title: 'Avatar' },
            { title: 'Código' },
            { title: 'Nombre' },
            { title: 'Ciudad' },
            { title: 'Círculos' },
            { title: 'Última acción' },
            { title: 'Estado' },
            { title: 'Acciones' },
          ]}
          selectable={false}
        >
          {agents.map((agent, idx) => (
            <IndexTable.Row key={agent.id} id={agent.id} position={idx}>
              <IndexTable.Cell>
                <div style={{ width: 40, height: 40 }}>
                  {agent.avatarUrl ? (
                    <Thumbnail source={agent.avatarUrl} alt={agent.name} size="small" />
                  ) : (
                    <div style={{
                      width: 40, height: 40, borderRadius: 20,
                      background: '#F3F6E7', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 16,
                    }}>🤖</div>
                  )}
                  <label style={{ fontSize: 10, color: '#7E942B', cursor: 'pointer' }}>
                    {uploadingAvatar === agent.id ? '...' : '📷'}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleAvatarUpload(agent.id, f);
                      }}
                    />
                  </label>
                </div>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge>{agent.code}</Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodyMd" as="p" fontWeight="semibold">
                  {agent.name}{agent.age ? `, ${agent.age}` : ''}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p" tone="subdued">{agent.city ?? '—'}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p" tone="subdued">
                  {(agent.circles ?? []).join(', ') || '—'}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" as="p" tone="subdued">
                  {agent.lastActionAt ? formatDate(agent.lastActionAt) : '—'}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge tone={agent.active ? 'success' : undefined}>
                  {agent.active ? 'Activo' : 'Inactivo'}
                </Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <ButtonGroup>
                  <Button size="slim" onClick={() => openEdit(agent)}>Editar</Button>
                  <Button size="slim" onClick={() => handleToggle(agent)}>
                    {agent.active ? 'Desactivar' : 'Activar'}
                  </Button>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <Button size="slim" variant="primary" loading={runningAgent === agent.id}
                      onClick={() => setRunMenuAgent(runMenuAgent === agent.id ? null : agent.id)}
                      disclosure={runMenuAgent === agent.id ? 'up' : 'down'}
                    >
                      ▶ Ejecutar
                    </Button>
                    {runMenuAgent === agent.id && (
                      <div style={{
                        position: 'absolute', top: '100%', right: 0, zIndex: 100,
                        background: '#fff', border: '1px solid #E1E3E5', borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 180,
                        marginTop: 4, overflow: 'hidden',
                      }}>
                        {[
                          { key: undefined, label: '🎲 Aleatorio', desc: 'Acción automática' },
                          { key: 'post', label: '📝 Post', desc: 'Crear nuevo post' },
                          { key: 'comment', label: '💬 Comentario', desc: 'Comentar en un post' },
                          { key: 'reply', label: '↩️ Respuesta', desc: 'Responder a un comentario' },
                          { key: 'upvote', label: '👍 Upvote', desc: 'Votar posts' },
                        ].map((opt) => (
                          <button
                            key={opt.key ?? 'auto'}
                            onClick={() => handleRunAgent(agent, opt.key)}
                            style={{
                              display: 'block', width: '100%', padding: '10px 14px',
                              border: 'none', background: 'none', cursor: 'pointer',
                              textAlign: 'left', fontSize: 13,
                              borderBottom: '1px solid #F1F2F3',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#F6F6F7')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                          >
                            <div style={{ fontWeight: 500 }}>{opt.label}</div>
                            <div style={{ fontSize: 11, color: '#8C9196', marginTop: 2 }}>{opt.desc}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </ButtonGroup>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingAgent ? `Editar ${editingAgent.name} (${editingAgent.code})` : 'Nuevo agente'}
        primaryAction={{
          content: editingAgent ? 'Guardar' : 'Crear',
          onAction: handleSave,
          loading: saving,
          disabled: !form.name.trim() || !form.personality.trim() || (!editingAgent && !form.code.trim()),
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setShowModal(false) }]}
        size="large"
      >
        <Modal.Section>
          <FormLayout>
            {/* Avatar upload */}
            <BlockStack gap="200">
              <Text variant="bodyMd" as="p" fontWeight="semibold">Avatar del agente</Text>
              <InlineStack gap="400" blockAlign="center">
                {(modalAvatarPreview || modalAvatarFile) && (
                  <img
                    src={modalAvatarFile ? URL.createObjectURL(modalAvatarFile) : modalAvatarPreview!}
                    alt="Avatar"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 40 }}
                  />
                )}
                {!modalAvatarFile ? (
                  <div style={{ flex: 1 }}>
                    <DropZone
                      accept="image/*"
                      type="image"
                      onDrop={(_files, acceptedFiles) => {
                        if (acceptedFiles.length > 0) setModalAvatarFile(acceptedFiles[0]);
                      }}
                      allowMultiple={false}
                    >
                      <DropZone.FileUpload actionHint="o arrastra una imagen" />
                    </DropZone>
                  </div>
                ) : (
                  <BlockStack gap="100">
                    <Text variant="bodySm" as="p">{modalAvatarFile.name}</Text>
                    <button onClick={() => setModalAvatarFile(null)} style={{
                      background: 'none', border: 'none', color: '#bf0711',
                      cursor: 'pointer', fontSize: 12, padding: 0,
                    }}>Eliminar</button>
                  </BlockStack>
                )}
              </InlineStack>
            </BlockStack>

            <FormLayout.Group>
              {!editingAgent && (
                <TextField label="Código" value={form.code} onChange={(v) => setForm({ ...form, code: v })}
                  helpText="Ej: RS-01, ER-03" autoComplete="off" />
              )}
              <TextField label="Nombre" value={form.name} onChange={(v) => setForm({ ...form, name: v })} autoComplete="off" />
              <TextField label="Edad" type="number" value={form.age} onChange={(v) => setForm({ ...form, age: v })} autoComplete="off" />
              <TextField label="Ciudad" value={form.city} onChange={(v) => setForm({ ...form, city: v })} autoComplete="off" />
            </FormLayout.Group>

            <TextField label="Personalidad (system prompt)" value={form.personality}
              onChange={(v) => setForm({ ...form, personality: v })} multiline={8} autoComplete="off" />

            <TextField label="Visual Kit (JSON — entorno, estilo fotográfico, elementos recurrentes)" value={form.visualKit}
              onChange={(v) => setForm({ ...form, visualKit: v })} multiline={6} autoComplete="off" />

            <TextField label="Prompt de retrato (para fotos donde aparece)" value={form.portraitPrompt}
              onChange={(v) => setForm({ ...form, portraitPrompt: v })} multiline={3} autoComplete="off" />

            <TextField label="Temas (separados por coma)" value={form.topics}
              onChange={(v) => setForm({ ...form, topics: v })} autoComplete="off"
              helpText="Ej: smoothies, desayunos rápidos, snacks de estudio" />

            <TextField label="Productos que usa (separados por coma)" value={form.products}
              onChange={(v) => setForm({ ...form, products: v })} autoComplete="off" />

            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" fontWeight="semibold">Categorías donde publica</Text>
              <InlineStack gap="300" wrap>
                {categories
                  .filter(cat => cat.market === (form.market || selectedMarket))
                  .map(cat => (
                    <Checkbox
                      key={cat.slug}
                      label={`${cat.name} (${cat.slug})`}
                      checked={form.circles.includes(cat.slug)}
                      onChange={(checked) => {
                        const updated = checked
                          ? [...form.circles, cat.slug]
                          : form.circles.filter(s => s !== cat.slug);
                        setForm({ ...form, circles: updated });
                      }}
                    />
                  ))
                }
              </InlineStack>
              {categories.filter(cat => cat.market === (form.market || selectedMarket)).length === 0 && (
                <Text as="p" tone="subdued">No hay categorías para este mercado</Text>
              )}
            </BlockStack>

            <TextField label="Reglas estrictas" value={form.strictRules}
              onChange={(v) => setForm({ ...form, strictRules: v })} multiline={4} autoComplete="off" />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Product Catalog Modal */}
      <ProductCatalogModal
        open={showProducts}
        onClose={() => setShowProducts(false)}
        onSuccess={onSuccess}
      />
    </>
  );
}

// ═══════════════════════════════════════
// Product Catalog Modal
// ═══════════════════════════════════════

interface CatalogProduct {
  id: string;
  slug: string;
  name: string;
  imageUrl: string | null;
  keywords: string[];
  sortOrder: number;
  active: boolean;
}

function ProductCatalogModal({ open, onClose, onSuccess }: {
  open: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}) {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState<CatalogProduct | null>(null);
  const [editForm, setEditForm] = useState({ name: '', keywords: '', imageUrl: '' });
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ slug: '', name: '', keywords: '', imageUrl: '' });
  const [creating, setCreating] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const data = await apiGet<{ products: CatalogProduct[] }>('/panel-api/community/products');
      setProducts(data.products);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { setLoading(true); loadProducts(); }
  }, [open, loadProducts]);

  const openEdit = (p: CatalogProduct) => {
    setEditProduct(p);
    setEditForm({
      name: p.name,
      keywords: (p.keywords ?? []).join(', '),
      imageUrl: p.imageUrl ?? '',
    });
  };

  const handleSave = async () => {
    if (!editProduct) return;
    setSaving(true);
    try {
      await apiPut(`/panel-api/community/products/${editProduct.id}`, {
        name: editForm.name,
        keywords: editForm.keywords.split(',').map(s => s.trim()).filter(Boolean),
        imageUrl: editForm.imageUrl || null,
      });
      setEditProduct(null);
      onSuccess('Producto actualizado');
      loadProducts();
    } catch (err: any) {
      alert(err.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (productId: string, file: File) => {
    setUploadingId(productId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const authHeader = await getAuthHeaderFn();
      const API_BASE = import.meta.env.VITE_API_URL ?? 'https://app.ohmygoods.com';
      const res = await fetch(`${API_BASE}/panel-api/community/products/${productId}/upload-image`, {
        method: 'POST',
        headers: { Authorization: authHeader },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const { publicUrl } = await res.json();
      onSuccess('Imagen actualizada');
      // If editing this product, update preview
      if (editProduct?.id === productId) {
        setEditForm(f => ({ ...f, imageUrl: publicUrl }));
      }
      loadProducts();
    } catch (err) {
      console.error('Failed to upload product image:', err);
    } finally {
      setUploadingId(null);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await apiPost('/panel-api/community/products', {
        slug: createForm.slug,
        name: createForm.name,
        keywords: createForm.keywords.split(',').map(s => s.trim()).filter(Boolean),
        imageUrl: createForm.imageUrl || undefined,
        sortOrder: products.length + 1,
      });
      setShowCreate(false);
      setCreateForm({ slug: '', name: '', keywords: '', imageUrl: '' });
      onSuccess('Producto añadido');
      loadProducts();
    } catch (err: any) {
      alert(err.message ?? 'Error al crear');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (p: CatalogProduct) => {
    if (!confirm(`¿Eliminar "${p.name}" del catálogo?`)) return;
    try {
      await apiDelete(`/panel-api/community/products/${p.id}`);
      onSuccess(`${p.name} eliminado`);
      loadProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
  };

  const handleToggle = async (p: CatalogProduct) => {
    try {
      await apiPut(`/panel-api/community/products/${p.id}`, { active: !p.active });
      onSuccess(p.active ? `${p.name} desactivado` : `${p.name} activado`);
      loadProducts();
    } catch (err) {
      console.error('Failed to toggle product:', err);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="📦 Catálogo de productos — Referencia visual para IA"
      size="large"
    >
      <Modal.Section>
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="bodySm" as="p" tone="subdued">
            Estas imágenes se envían al modelo de IA como referencia visual cuando un agente genera contenido sobre un producto.
          </Text>
          <Button variant="primary" onClick={() => setShowCreate(true)}>+ Añadir producto</Button>
        </InlineStack>
      </Modal.Section>
      <Modal.Section>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center' }}><Spinner /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {products.map(p => (
              <div key={p.id} style={{
                border: '1px solid #E1E3E5',
                borderRadius: 12,
                padding: 12,
                opacity: p.active ? 1 : 0.5,
                background: p.active ? '#fff' : '#FAFBFB',
              }}>
                {/* Image */}
                <div style={{
                  width: '100%', height: 140, borderRadius: 8, marginBottom: 10,
                  background: '#F6F6F7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', position: 'relative',
                }}>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Text variant="bodySm" as="p" tone="subdued">Sin imagen</Text>
                  )}
                  {/* Upload overlay */}
                  <label style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: '4px 8px',
                    cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#7E942B',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}>
                    {uploadingId === p.id ? '⏳' : '📷 Cambiar'}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleImageUpload(p.id, f);
                      }}
                    />
                  </label>
                </div>

                {/* Name & keywords */}
                <Text variant="bodyMd" as="p" fontWeight="semibold">{p.name}</Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  {(p.keywords ?? []).join(', ') || 'Sin keywords'}
                </Text>

                {/* Actions */}
                <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                  <button onClick={() => openEdit(p)} style={{
                    background: 'none', border: '1px solid #C9CCCF', borderRadius: 6,
                    padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                  }}>✏️ Editar</button>
                  <button onClick={() => handleToggle(p)} style={{
                    background: 'none', border: '1px solid #C9CCCF', borderRadius: 6,
                    padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                    color: p.active ? '#bf0711' : '#2C6ECB',
                  }}>{p.active ? 'Desactivar' : 'Activar'}</button>
                  <button onClick={() => handleDelete(p)} style={{
                    background: 'none', border: '1px solid #C9CCCF', borderRadius: 6,
                    padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                    color: '#bf0711',
                  }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Section>

      {/* Create product sub-modal */}
      {showCreate && (
        <Modal
          open={true}
          onClose={() => setShowCreate(false)}
          title="Añadir producto al catálogo"
          primaryAction={{
            content: 'Crear',
            onAction: handleCreate,
            loading: creating,
            disabled: !createForm.slug.trim() || !createForm.name.trim(),
          }}
          secondaryActions={[{ content: 'Cancelar', onAction: () => setShowCreate(false) }]}
        >
          <Modal.Section>
            <FormLayout>
              <TextField label="Slug (identificador único)" value={createForm.slug}
                onChange={(v) => setCreateForm({ ...createForm, slug: v })} autoComplete="off"
                helpText="Ej: matcha-proteina, kit-inicio" />
              <TextField label="Nombre del producto" value={createForm.name}
                onChange={(v) => setCreateForm({ ...createForm, name: v })} autoComplete="off" />
              <TextField label="URL de imagen (opcional)" value={createForm.imageUrl}
                onChange={(v) => setCreateForm({ ...createForm, imageUrl: v })} autoComplete="off"
                helpText="Puedes subirla después desde la tarjeta del producto" />
              <TextField label="Keywords multiidioma (separadas por coma)" value={createForm.keywords}
                onChange={(v) => setCreateForm({ ...createForm, keywords: v })} autoComplete="off"
                multiline={3}
                helpText="Incluye variantes en todos los idiomas: es, en, de, fr, it, pt, nl, pl" />
            </FormLayout>
          </Modal.Section>
        </Modal>
      )}

      {/* Inline edit sub-modal */}
      {editProduct && (
        <Modal
          open={true}
          onClose={() => setEditProduct(null)}
          title={`Editar: ${editProduct.name}`}
          primaryAction={{ content: 'Guardar', onAction: handleSave, loading: saving }}
          secondaryActions={[{ content: 'Cancelar', onAction: () => setEditProduct(null) }]}
        >
          <Modal.Section>
            <FormLayout>
              {editForm.imageUrl && (
                <div style={{
                  display: 'flex', justifyContent: 'center', padding: 12,
                  background: '#F6F6F7', borderRadius: 8, marginBottom: 8,
                }}>
                  <img src={editForm.imageUrl} alt={editForm.name}
                    style={{ maxHeight: 120, objectFit: 'contain' }} />
                </div>
              )}
              <TextField label="Nombre" value={editForm.name}
                onChange={(v) => setEditForm({ ...editForm, name: v })} autoComplete="off" />
              <TextField label="URL de imagen" value={editForm.imageUrl}
                onChange={(v) => setEditForm({ ...editForm, imageUrl: v })} autoComplete="off"
                helpText="URL directa de la imagen (Shopify CDN, R2, etc.)" />
              <TextField label="Keywords (separadas por coma)" value={editForm.keywords}
                onChange={(v) => setEditForm({ ...editForm, keywords: v })} autoComplete="off"
                helpText="El orquestador busca estas palabras para decidir qué imagen enviar al LLM" />
            </FormLayout>
          </Modal.Section>
        </Modal>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════
// Moderation Tab (pending_review posts)
// ═══════════════════════════════════════

function ModerationTab({ onSuccess }: { onSuccess: (msg: string) => void }) {
  const [posts, setPosts] = useState<PendingPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadPosts = useCallback(async () => {
    try {
      const data = await apiGet<{ posts: PendingPost[] }>('/panel-api/community/moderation/pending');
      setPosts(data.posts);
    } catch (err) {
      console.error('Failed to load pending posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleApprove = async (postId: string) => {
    setActionLoading(postId);
    try {
      await apiPost(`/panel-api/community/moderation/${postId}/approve`, {});
      setPosts(prev => prev.filter(p => p.id !== postId));
      onSuccess('Post aprobado');
    } catch (err) {
      console.error('Failed to approve:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectId || !rejectReason.trim()) return;
    setActionLoading(rejectId);
    try {
      await apiPost(`/panel-api/community/moderation/${rejectId}/reject`, { reason: rejectReason.trim() });
      setPosts(prev => prev.filter(p => p.id !== rejectId));
      setRejectId(null);
      setRejectReason('');
      onSuccess('Post rechazado');
    } catch (err) {
      console.error('Failed to reject:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="small" />
        </div>
      </Card>
    );
  }

  if (posts.length === 0) {
    return (
      <Card>
        <EmptyState heading="Sin posts pendientes" image="">
          <p>No hay posts esperando moderación.</p>
        </EmptyState>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingSm">Posts pendientes de moderación ({posts.length})</Text>
          <IndexTable
            headings={[
              { title: 'Autor' },
              { title: 'Título' },
              { title: 'Contenido' },
              { title: 'Fecha' },
              { title: 'Acciones' },
            ]}
            itemCount={posts.length}
            selectable={false}
          >
            {posts.map((post, idx) => (
              <IndexTable.Row key={post.id} id={post.id} position={idx}>
                <IndexTable.Cell>
                  <Text as="span" variant="bodyMd" fontWeight="semibold">{post.author.name}</Text>
                  <br />
                  <Text as="span" variant="bodySm" tone="subdued">{post.author.email}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" variant="bodyMd">{post.title}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" variant="bodySm" tone="subdued">
                    {post.bodyPlain.length > 100 ? post.bodyPlain.slice(0, 100) + '...' : post.bodyPlain}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" variant="bodySm">{formatDate(post.createdAt)}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <ButtonGroup>
                    <Button
                      variant="primary"
                      size="slim"
                      onClick={() => handleApprove(post.id)}
                      loading={actionLoading === post.id}
                    >
                      Aprobar
                    </Button>
                    <Button
                      variant="primary"
                      tone="critical"
                      size="slim"
                      onClick={() => { setRejectId(post.id); setRejectReason(''); }}
                      disabled={actionLoading === post.id}
                    >
                      Rechazar
                    </Button>
                  </ButtonGroup>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        </BlockStack>
      </Card>

      {/* Reject modal */}
      <Modal
        open={!!rejectId}
        onClose={() => setRejectId(null)}
        title="Rechazar post"
        primaryAction={{
          content: 'Rechazar',
          destructive: true,
          onAction: handleReject,
          loading: !!actionLoading,
          disabled: !rejectReason.trim(),
        }}
        secondaryActions={[{
          content: 'Cancelar',
          onAction: () => setRejectId(null),
        }]}
      >
        <Modal.Section>
          <TextField
            label="Motivo del rechazo"
            value={rejectReason}
            onChange={setRejectReason}
            multiline={3}
            autoComplete="off"
            placeholder="Explica brevemente por qué se rechaza este post..."
          />
        </Modal.Section>
      </Modal>
    </>
  );
}
